import React, { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Settings } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ListCustomFieldsEditTab } from "./ListCustomFieldsEditTab";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { List } from "@/types/list";

interface ListEditTabsProps {
  list: List;
  isEditing: boolean;
  onChange: (field: string, value: string) => void;
  onCustomFieldsChange?: (customFields: Record<string, any>) => void;
  customFieldsConfig?: any[]; // Custom fields configuration for the organization
}

export const ListEditTabs: React.FC<ListEditTabsProps> = ({ 
  list, 
  isEditing, 
  onChange,
  onCustomFieldsChange,
  customFieldsConfig = []
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('basic');

  const handleFieldChange = (field: string, value: string) => {
    onChange(field, value);
  };

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full flex flex-wrap gap-1 bg-gray-100 rounded-sm p-1">
          <TabsTrigger value="basic" className="flex-shrink-0 text-xs sm:text-sm">
            {t('lists.editTabs.basicInfo', 'Basic Info')}
          </TabsTrigger>
          {customFieldsConfig.length > 0 && (
            <TabsTrigger value="customFields" className="flex-shrink-0 text-xs sm:text-sm">
              {t('lists.customFields.title', 'Custom Fields')}
            </TabsTrigger>
          )}
        </TabsList>
        
        <TabsContent value="basic" className="mt-6">
          <div className="space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">
                {t('lists.editTabs.basicInformation', 'Basic Information')}
              </h3>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name" className="text-sm font-medium">
                    {t('lists.form.name', 'List Name')} *
                  </Label>
                  <Input
                    id="name"
                    value={list.name || ''}
                    onChange={(e) => handleFieldChange('name', e.target.value)}
                    placeholder={t('lists.form.namePlaceholder', 'Enter list name')}
                    disabled={!isEditing}
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label htmlFor="description" className="text-sm font-medium">
                    {t('lists.form.description', 'Description')}
                  </Label>
                  <Textarea
                    id="description"
                    value={list.description || ''}
                    onChange={(e) => handleFieldChange('description', e.target.value)}
                    placeholder={t('lists.form.descriptionPlaceholder', 'Enter list description (optional)')}
                    disabled={!isEditing}
                    className="mt-1"
                    rows={3}
                  />
                </div>
              </div>
            </div>



          </div>
        </TabsContent>
        
        {customFieldsConfig.length > 0 && (
          <TabsContent value="customFields" className="mt-6">
            <ListCustomFieldsEditTab
              list={list}
              onChange={onCustomFieldsChange || (() => {})}
              isEditing={isEditing}
            />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};
